/**
 * Utility functions for JWT token operations
 */

export interface JwtPayload {
  exp?: number;
  iat?: number;
  [key: string]: any;
}

/**
 * Decode JWT token without verification
 * @param token JWT token string
 * @returns Decoded payload or null if invalid
 */
export function decodeJwt(token: string): JwtPayload | null {
  try {
    console.log('[JWT] decodeJwt: Starting decode, token length:', token?.length);
    const parts = token.split('.');
    console.log('[JWT] decodeJwt: Token parts count:', parts.length);
    
    if (parts.length !== 3) {
      console.error('[JWT] decodeJwt: Invalid token format, expected 3 parts, got:', parts.length);
      return null;
    }
    
    const payload = parts[1];
    console.log('[JWT] decodeJwt: Payload part length:', payload.length);
    
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed = JSON.parse(decoded);
    
    console.log('[JWT] decodeJwt: Decoded payload:', {
      hasExp: 'exp' in parsed,
      hasIat: 'iat' in parsed,
      exp: parsed.exp,
      iat: parsed.iat,
      expDate: parsed.exp ? new Date(parsed.exp * 1000).toISOString() : null,
      iatDate: parsed.iat ? new Date(parsed.iat * 1000).toISOString() : null,
      allKeys: Object.keys(parsed)
    });
    
    return parsed;
  } catch (error) {
    console.error('[JWT] decodeJwt: Error decoding JWT:', error);
    return null;
  }
}

/**
 * Check if token is expired
 * @param token JWT token string
 * @returns true if token is expired or invalid
 */
export function isTokenExpired(token: string): boolean {
  const payload = decodeJwt(token);
  if (!payload || !payload.exp) {
    console.log('[JWT] isTokenExpired: No expiration info, returning true');
    return true;
  }
  
  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const isExpired = currentTime >= expirationTime;
  
  const timeUntilExpiration = expirationTime - currentTime;
  console.log('[JWT] isTokenExpired:', {
    expirationTime: new Date(expirationTime).toISOString(),
    currentTime: new Date(currentTime).toISOString(),
    timeUntilExpirationMinutes: Math.round(timeUntilExpiration / 60000),
    isExpired
  });
  
  return isExpired;
}

/**
 * Check if token should be refreshed (at 2/3 of its lifetime)
 * @param token JWT token string
 * @returns true if token should be refreshed
 */
export function shouldRefreshToken(token: string): boolean {
  const payload = decodeJwt(token);
  if (!payload || !payload.exp || !payload.iat) {
    console.log('[JWT] shouldRefreshToken: Cannot determine token info, returning true (safe refresh)');
    return true; // If we can't determine, refresh to be safe
  }
  
  const expirationTime = payload.exp * 1000;
  const issuedTime = payload.iat * 1000;
  const currentTime = Date.now();
  
  // Calculate token lifetime
  const tokenLifetime = expirationTime - issuedTime;
  
  // Refresh at 2/3 of token lifetime (proactive refresh)
  const refreshThreshold = issuedTime + (tokenLifetime * 2 / 3);
  const shouldRefresh = currentTime >= refreshThreshold;
  
  const timeUntilExpiration = expirationTime - currentTime;
  const timeUntilRefreshThreshold = refreshThreshold - currentTime;
  
  // Format time helper
  const formatTime = (ms: number): string => {
    if (ms < 0) return 'Đã qua';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };
  
  console.log('[JWT] shouldRefreshToken: ===== TOKEN TIMING INFO =====');
  console.log('[JWT] shouldRefreshToken: Issued at:', new Date(issuedTime).toLocaleString('vi-VN'));
  console.log('[JWT] shouldRefreshToken: Current time:', new Date(currentTime).toLocaleString('vi-VN'));
  console.log('[JWT] shouldRefreshToken: Expires at:', new Date(expirationTime).toLocaleString('vi-VN'));
  console.log('[JWT] shouldRefreshToken: Refresh threshold (2/3):', new Date(refreshThreshold).toLocaleString('vi-VN'));
  console.log('[JWT] shouldRefreshToken: Token lifetime:', formatTime(tokenLifetime), `(${Math.round(tokenLifetime / 60000)} phút)`);
  console.log('[JWT] shouldRefreshToken: ⏱️  Thời gian còn lại đến khi hết hạn:', formatTime(timeUntilExpiration), `(${Math.round(timeUntilExpiration / 60000)} phút)`);
  console.log('[JWT] shouldRefreshToken: ⏱️  Thời gian còn lại đến khi refresh (2/3):', formatTime(timeUntilRefreshThreshold), `(${Math.round(timeUntilRefreshThreshold / 60000)} phút)`);
  console.log('[JWT] shouldRefreshToken: Should refresh now?', shouldRefresh ? '✅ YES (đã đạt 2/3 thời gian sống)' : '❌ NO (chưa đạt 2/3 thời gian sống)');
  console.log('[JWT] shouldRefreshToken: ============================================');
  
  return shouldRefresh;
}

/**
 * Get time until token expires in milliseconds
 * @param token JWT token string
 * @returns milliseconds until expiration, or 0 if expired/invalid
 */
export function getTimeUntilExpiration(token: string): number {
  const payload = decodeJwt(token);
  if (!payload || !payload.exp) {
    return 0;
  }
  
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const timeUntilExpiration = expirationTime - currentTime;
  
  return Math.max(0, timeUntilExpiration);
}

/**
 * Get detailed token timing information
 * @param token JWT token string
 * @returns Object with timing information or null if invalid
 */
export function getTokenTimingInfo(token: string): {
  issuedAt: Date;
  expiresAt: Date;
  refreshAt: Date;
  currentTime: Date;
  timeUntilExpiration: number;
  timeUntilRefresh: number;
  tokenLifetime: number;
  timeUntilExpirationFormatted: string;
  timeUntilRefreshFormatted: string;
  tokenLifetimeFormatted: string;
} | null {
  const payload = decodeJwt(token);
  if (!payload || !payload.exp || !payload.iat) {
    return null;
  }
  
  const issuedTime = payload.iat * 1000;
  const expirationTime = payload.exp * 1000;
  const currentTime = Date.now();
  const tokenLifetime = expirationTime - issuedTime;
  const refreshThreshold = issuedTime + (tokenLifetime * 2 / 3);
  
  const timeUntilExpiration = expirationTime - currentTime;
  const timeUntilRefresh = refreshThreshold - currentTime;
  
  // Format time helper
  const formatTime = (ms: number): string => {
    if (ms < 0) return 'Đã qua';
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  };
  
  return {
    issuedAt: new Date(issuedTime),
    expiresAt: new Date(expirationTime),
    refreshAt: new Date(refreshThreshold),
    currentTime: new Date(currentTime),
    timeUntilExpiration,
    timeUntilRefresh,
    tokenLifetime,
    timeUntilExpirationFormatted: formatTime(timeUntilExpiration),
    timeUntilRefreshFormatted: formatTime(timeUntilRefresh),
    tokenLifetimeFormatted: formatTime(tokenLifetime)
  };
}

